/**
 * SoleadLab 2.0 - Monitoring Store (v2)
 *
 * Full rewrite with Zod-validated types, real API integration,
 * compound alarm evaluation, SSE streaming, and strict
 * config vs runtime state separation.
 *
 * State groups:
 * - Config State (persisted to backend): projectConfig, isConfigDirty, dataMode
 * - Schema State: schema, isSchemaLoaded
 * - Runtime State (derived, never saved): runtimeStates Map, lastRefreshAt, refreshError
 * - Connection State: providerStatus, credentialStatus, sseConnected
 * - Alarm State: activeAlarms, alarmHistory, alarmSoundEnabled
 * - Stream State: isStreaming, streamError, reconnectCount
 * - Site State: scadaSites, selectedSiteId
 */

import { create } from 'zustand';
import type {
  ProjectMonitoringConfig,
  DataPointConfig,
  DataPointRuntimeState,
  ActiveAlarmEvent,
  CredentialStatus,
  ParsedSchema,
  AlarmRuleGroup,
  AlarmRule,
  DataMode,
  ProviderStatus,
  ProviderInfo,
  ProviderHealth,
  MonitoringUIState,
  AdvancedAlarmRule,
  TriggeredAdvancedRule,
  MatchedMetric,
  RuleExclusion,
  WSSnapshotMessage,
} from '@/types/monitoring';
import * as api from '@/services/monitoringApi';
/** Union of the two canonical granular-write result shapes (config-column vs globals). */
type CanonicalWriteResult = api.CanonicalConfigWriteResult | api.CanonicalGlobalsWriteResult;
import { evaluateAllDataPoints, extractEntityValues, evaluateAdvancedRules } from '@/services/alarmEvaluator';
import { createDataStream, DataStreamManager } from '@/services/dataStream';
import { playAlarmSound, setAlarmSoundEnabled as setSoundEnabled } from '@/services/alarmSound';
import { useAuthStore } from './authStore';

// ============================================
// Legacy Type Exports (backward compatibility)
// ============================================

/** @deprecated Use AlarmSeverity from @/types/monitoring */
export type AlarmSeverity = 'critical' | 'warning' | 'info';
/** @deprecated Use AlarmOperator from @/types/monitoring */
export type AlarmOperator = 'gt' | 'lt' | 'gte' | 'lte' | 'eq' | 'neq' | 'between' | 'outside';
/** @deprecated Use DataPointConfig from @/types/monitoring */
export type DataPointValueType = 'number' | 'string' | 'boolean' | 'enum';

/** @deprecated Use DataPointConfig from @/types/monitoring */
export interface DataPoint {
  id: string;
  name: string;
  displayName: string;
  unit?: string;
  valueType: DataPointValueType;
  sourceId?: string;
  coordinates?: [number, number];
  visible: boolean;
  color?: string;
  decimalPlaces?: number;
  showTrend?: boolean;
  trendPeriod?: number;
  valueMap?: Record<string, string>;
  alarms: Alarm[];
  currentValue?: number | string | boolean;
  lastUpdated?: string;
  status?: 'ok' | 'warning' | 'critical' | 'offline';
}

/** @deprecated Use AlarmRule from @/types/monitoring */
export interface Alarm {
  id: string;
  name: string;
  severity: AlarmSeverity;
  operator: AlarmOperator;
  threshold: number;
  threshold2?: number;
  message?: string;
  createTask?: boolean;
  notifyEmail?: boolean;
}

/** @deprecated Use ActiveAlarmEvent from @/types/monitoring */
export interface ActiveAlarm {
  id: string;
  dataPointId: string;
  dataPointName: string;
  alarmId: string;
  alarmName: string;
  severity: AlarmSeverity;
  value: number;
  threshold: number;
  message: string;
  triggeredAt: string;
  acknowledgedAt?: string;
  acknowledgedBy?: string;
}

export interface SLDPointMapping {
  dataPointId: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  displayMetric?: string;
}

export interface SLDAdvancedRuleMapping {
  advancedRuleId: string;
  x: number;
  y: number;
}

export interface SLDDiagram {
  id: string;
  name: string;
  imageUrl: string;
  dataPointMappings: SLDPointMapping[];
  advancedRuleMappings?: SLDAdvancedRuleMapping[];
  /** Google Drive file ID of the source PDF — dedup key so a re-import of the
   *  same Drive folder can skip it (P1 undo/history). */
  driveFileId?: string;
}

export interface ScadaConfig {
  // NOTE: an `apiUrl` field used to live here, defaulted to the long-dead
  // scada-api.soleadlab.com (the Jan-2026 Vultr prototype, decommissioned
  // 2026-09-01) and read by NOTHING — live SCADA polling is server-side in the
  // SiteMonitor DO. Removed; old persisted configs carrying the key parse fine.
  siteId?: string;
  refreshInterval: number;
  isConnected: boolean;
  lastSync?: string;
  lastRefresh?: string;
  error?: string;
  alarmSoundEnabled: boolean;
}

// ============================================
// Store State Interface
// ============================================

interface MonitoringState {
  // --- Config State (persisted to backend) ---
  projectConfig: ProjectMonitoringConfig | null;
  isConfigDirty: boolean;
  dataMode: DataMode;
  isConfigLoading: boolean;
  configError: string | null;
  // B2 — canonical D1 config version (CAS token for safe writes) + the site it belongs to.
  // configVersion is set on every D1 load; d1ConfigSiteId guards a stale blob snapshot from
  // overwriting the D1-hydrated config for the active site.
  configVersion: number | null;
  d1ConfigSiteId: string | null;
  // B3a — true while a canonical granular write is in flight. Serializes writes so two
  // overlapping edits can't race the version (the second would 409 anyway); a duplicate
  // concurrent write is blocked (no-op) rather than sent.
  configWriteInFlight: boolean;

  // --- Schema State ---
  schema: ParsedSchema | null;
  isSchemaLoaded: boolean;

  // --- Runtime State (derived per refresh, NEVER saved) ---
  runtimeStates: Map<string, DataPointRuntimeState>;
  lastRefreshAt: string | null;
  refreshError: string | null;
  uploadedSampleData: Record<string, Record<string, number | string | null>> | null;

  // --- Connection State ---
  providerStatus: ProviderStatus;
  credentialStatus: CredentialStatus | null;
  providers: ProviderInfo[];

  // --- Alarm State ---
  activeAlarms: ActiveAlarmEvent[];
  alarmHistory: ActiveAlarmEvent[];
  alarmSoundEnabled: boolean;
  triggeredAdvancedRules: TriggeredAdvancedRule[];
  // Live-mode display overlay: matched offenders per advanced rule, computed from
  // current frontend runtime data. Keyed by rule_id. DISPLAY METADATA ONLY — never
  // an alarm source (does not create/remove/count/persist alarms).
  advancedMatchedByRule: Record<string, MatchedMetric[]>;

  // --- Provider Health (from DO) ---
  providerHealth: ProviderHealth | null;
  monitoringUIState: MonitoringUIState;
  invalidDatapointIds: string[];
  schemaHash: string;

  // --- Stream State ---
  isStreaming: boolean;
  streamError: string | null;

  // --- Site State ---
  scadaSites: Array<{ id: string; name: string; description?: string; client_id?: string }>;
  selectedSiteId: string | null;
  isFetchingSites: boolean;

  // --- SLD State (carried forward) ---
  sldDiagrams: SLDDiagram[];
  activeSldId: string | null;

  // --- Legacy compat (derived from config) ---
  dataPoints: DataPoint[];
  scadaConfig: ScadaConfig;
  isAutoRefreshEnabled: boolean;
  isRefreshing: boolean;

  // ============================================
  // Config Actions
  // ============================================
  loadConfig: (siteId: string) => Promise<void>;
  saveConfig: () => Promise<boolean>;
  // B3a — shared plumbing for a single canonical granular write: requires configVersion,
  // serializes overlapping writes, sends expectedVersion, applies the returned config (or
  // globals) + version, and handles 409 by reloading D1 + surfacing a conflict (no silent
  // retry). Returns true only on a confirmed successful write.
  runCanonicalWrite: (
    doWrite: (siteId: string, expectedVersion: number) => Promise<CanonicalWriteResult>,
    opts?: { kind?: 'config' | 'globals'; afterApply?: (cfg: ProjectMonitoringConfig) => Partial<MonitoringState> },
  ) => Promise<boolean>;
  setDataMode: (mode: DataMode) => void;
  // B3a — canonical POST (new datapoint). Returns true on confirmed write.
  addDataPointConfig: (config: DataPointConfig) => Promise<boolean>;
  // B2 interim (B3b): existing-datapoint modal edit stays local-mutate + Publish. Do NOT
  // route this through a canonical write — the bundled metadata + nested rules/rule_groups
  // save needs the B3b diff-sync decomposition.
  updateDataPointConfig: (id: string, updates: Partial<DataPointConfig>) => void;
  // B3a — canonical DELETE.
  removeDataPointConfig: (id: string) => Promise<boolean>;
  addAlarmRule: (datapointId: string, rule: AlarmRule) => Promise<boolean>;
  removeAlarmRule: (datapointId: string, ruleId: string) => Promise<boolean>;
  addAlarmRuleGroup: (datapointId: string, group: AlarmRuleGroup) => Promise<boolean>;
  removeAlarmRuleGroup: (datapointId: string, groupId: string) => Promise<boolean>;
  // B3a — optimistic LOCAL globals merge only (no write); keeps controlled inputs responsive
  // while typing an incomplete value. Persist with updateGlobals.
  setGlobalsLocal: (globalsPartial: Partial<NonNullable<ProjectMonitoringConfig['globals']>>) => void;
  // B3a — canonical PATCH /globals with only the changed keys (server shallow-merges).
  updateGlobals: (globalsPartial: Partial<NonNullable<ProjectMonitoringConfig['globals']>>) => Promise<boolean>;

  // Advanced rule CRUD (site-level) — B3a canonical writes
  addAdvancedRule: (rule: AdvancedAlarmRule) => Promise<boolean>;
  updateAdvancedRule: (id: string, updates: Partial<AdvancedAlarmRule>) => Promise<boolean>;
  removeAdvancedRule: (id: string) => Promise<boolean>;
  // Exclude-from-alarm: append an {entity_id, metric} pair to an advanced rule's exclusions
  // and persist via the canonical advanced-rule PATCH (B3a). Deduped; safe no-op for an
  // unknown rule. Returns true iff the write succeeded.
  addRuleExclusion: (ruleId: string, exclusion: RuleExclusion) => Promise<boolean>;

  // ============================================
  // Schema Actions
  // ============================================
  loadSchema: (siteId: string) => Promise<void>;
  setSchema: (schema: ParsedSchema) => void;
  resetProjectState: () => void;
  setProjectConfig: (config: ProjectMonitoringConfig) => void;
  setUploadedSampleData: (data: Record<string, Record<string, number | string | null>>) => void;

  // ============================================
  // Data Actions
  // ============================================
  pushUploadData: (jsonPayload: unknown) => Promise<void>;
  refreshData: () => Promise<void>;
  updateRuntimeData: (
    entities: Record<string, Record<string, number | string | null>>,
    timestamp: string
  ) => void;

  // ============================================
  // Stream Actions
  // ============================================
  startStream: () => void;
  stopStream: () => void;

  // ============================================
  // Live Mode Actions
  // ============================================
  hydrateFromSnapshot: (snapshot: WSSnapshotMessage) => void;
  startLive: () => Promise<void>;
  stopLive: () => Promise<void>;
  refreshLive: () => Promise<void>;
  discoverSchema: () => Promise<void>;
  deriveMonitoringUIState: () => MonitoringUIState;

  // ============================================
  // Credential Actions
  // ============================================
  loadProviders: () => Promise<void>;
  loadCredentialStatus: (siteId: string) => Promise<void>;
  testConnection: () => Promise<boolean>;

  // ============================================
  // Alarm Actions
  // ============================================
  loadAlarms: (siteId: string) => Promise<void>;
  acknowledgeAlarm: (alarmId: string, notes?: string) => Promise<void>;
  dismissAlarm: (id: string) => void;
  clearAlarmHistory: () => void;
  setAlarmSoundEnabled: (enabled: boolean) => void;

  // ============================================
  // Site Actions
  // ============================================
  fetchScadaSites: () => Promise<void>;
  selectSite: (siteId: string | null) => Promise<void>;
  addScadaSite: (name: string, description?: string) => Promise<void>;
  deleteScadaSite: (siteId: string) => Promise<void>;

  // ============================================
  // SLD Actions (carried forward)
  // ============================================
  addSLD: (sld: SLDDiagram) => void;
  updateSLD: (id: string, updates: Partial<SLDDiagram>) => void;
  removeSLD: (id: string) => void;
  setActiveSLD: (id: string | null) => void;

  // ============================================
  // Legacy Compat Actions
  // ============================================
  setScadaConfig: (config: Partial<ScadaConfig>) => void;
  setAutoRefresh: (enabled: boolean) => void;
  addDataPoint: (dataPoint: DataPoint) => void;
  updateDataPoint: (id: string, updates: Partial<DataPoint>) => void;
  removeDataPoint: (id: string) => void;
  setDataPointValue: (id: string, value: number | string | boolean) => void;
  addAlarmToDataPoint: (dataPointId: string, alarm: Alarm) => void;
  updateAlarm: (dataPointId: string, alarmId: string, updates: Partial<Alarm>) => void;
  removeAlarm: (dataPointId: string, alarmId: string) => void;
  triggerAlarm: (alarm: ActiveAlarm) => void;
  setRefreshing: (refreshing: boolean) => void;
  loadMonitoringConfig: (config: {
    dataPoints?: DataPoint[];
    sldDiagrams?: SLDDiagram[];
    scadaConfig?: Partial<ScadaConfig>;
  }) => void;
  exportMonitoringConfig: () => {
    dataPoints: DataPoint[];
    sldDiagrams: SLDDiagram[];
    scadaConfig: ScadaConfig;
  };
}

// ============================================
// Defaults
// ============================================

const defaultScadaConfig: ScadaConfig = {
  refreshInterval: 300,
  isConnected: false,
  alarmSoundEnabled: true,
};

/** Default config used when creating a new monitoring config for a site. */
export const DEFAULT_PROJECT_CONFIG: ProjectMonitoringConfig = {
  version: 1,
  provider_state: {
    connected: false,
    status: 'disconnected',
  },
  datapoints: [],
  advanced_rules: [],
};

// ============================================
// Stream Manager Singleton (not in store state)
// ============================================

let streamManager: DataStreamManager | null = null;

// ============================================
// B3a helpers
// ============================================

/**
 * afterApply for advanced-rule canonical writes: recompute the upload-mode triggered-rules
 * display from the freshly-returned config + current sample data, and clear the live
 * offender overlay (recomputed on the next runtime-data tick). Mirrors the display recompute
 * the pre-B3a local mutations did inline. Sample data / schema are captured by the caller at
 * action start (they don't change during the write).
 */
function advancedDisplayAfterApply(
  cfg: ProjectMonitoringConfig,
  uploadedSampleData: Record<string, Record<string, number | string | null>> | null,
  schema: ParsedSchema | null,
): Pick<MonitoringState, 'triggeredAdvancedRules' | 'advancedMatchedByRule'> {
  const data = uploadedSampleData && Object.keys(uploadedSampleData).length > 0 ? uploadedSampleData : null;
  return {
    triggeredAdvancedRules: data ? evaluateAdvancedRules(cfg.advanced_rules ?? [], data, schema) : [],
    advancedMatchedByRule: {},
  };
}

// ============================================
// Store
// ============================================

export const useMonitoringStore = create<MonitoringState>((set, get) => ({
  // Config State
  projectConfig: null,
  isConfigDirty: false,
  dataMode: 'upload',
  isConfigLoading: false,
  configError: null,
  configVersion: null,
  d1ConfigSiteId: null,
  configWriteInFlight: false,

  // Schema State
  schema: null,
  isSchemaLoaded: false,

  // Runtime State
  runtimeStates: new Map(),
  lastRefreshAt: null,
  refreshError: null,
  uploadedSampleData: null,

  // Connection State
  providerStatus: 'disconnected',
  credentialStatus: null,
  providers: [],

  // Alarm State
  activeAlarms: [],
  alarmHistory: [],
  alarmSoundEnabled: true,
  triggeredAdvancedRules: [],
  advancedMatchedByRule: {},

  // Provider Health
  providerHealth: null,
  monitoringUIState: 'NO_CREDENTIALS',
  invalidDatapointIds: [],
  schemaHash: '',

  // Stream State
  isStreaming: false,
  streamError: null,

  // Site State
  scadaSites: [],
  selectedSiteId: null,
  isFetchingSites: false,

  // SLD State
  sldDiagrams: [],
  activeSldId: null,

  // Legacy compat
  dataPoints: [],
  scadaConfig: defaultScadaConfig,
  isAutoRefreshEnabled: false,
  isRefreshing: false,

  // ============================================
  // Config Actions
  // ============================================

  loadConfig: async (siteId: string) => {
    set({ isConfigLoading: true, configError: null });
    try {
      // B2 — hydrate the canonical D1 config AND its version. D1 is the source of truth.
      const { config, version } = await api.getMonitoringConfig(siteId);
      set({
        projectConfig: config,
        configVersion: version,
        d1ConfigSiteId: siteId, // mark this site's config as D1-authoritative (blob guard)
        isConfigDirty: false,
        isConfigLoading: false,
        // New site's config (incl. advanced_rules) just loaded — drop any live
        // offender overlay from the previous config; next updateRuntimeData recomputes.
        advancedMatchedByRule: {},
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load config';
      set({ isConfigLoading: false, configError: message });
      console.error('loadConfig error:', err);
    }
  },

  // B2 — SAFE interim persist: non-destructive merge + optimistic-concurrency CAS.
  // Returns true only on a confirmed successful save. NEVER the legacy no-mode wholesale
  // PUT. Without a known configVersion we cannot merge safely, so we block and reload D1
  // instead of risking a stale write that could clobber D1-only config.
  saveConfig: async (): Promise<boolean> => {
    const { selectedSiteId, projectConfig, configVersion } = get();
    if (!selectedSiteId || !projectConfig) return false;

    if (configVersion == null) {
      set({ configError: 'Config version not loaded — reloading from server. Please retry.' });
      await get().loadConfig(selectedSiteId);
      return false;
    }

    set({ isConfigLoading: true, configError: null });
    try {
      // mode:'merge' + expectedVersion — cannot wipe D1-only definitions; 409 if stale.
      const res = await api.saveMonitoringConfig(selectedSiteId, projectConfig, configVersion);
      // Advance the CAS token from the server response. projectConfig already reflects the
      // user's edit (it was hydrated from D1 at load, so it holds the full config + the
      // change); the merge is non-destructive, so D1 and local now agree. If the server
      // omitted a version, reload to resync the token.
      if (res.version != null) {
        set({ configVersion: res.version, isConfigDirty: false, isConfigLoading: false });
      } else {
        await get().loadConfig(selectedSiteId);
        set({ isConfigDirty: false });
      }
      return true;
    } catch (err) {
      if (err instanceof api.ApiError && err.status === 409) {
        // Stale write — reload the latest D1 config, THEN surface the conflict (loadConfig
        // clears configError, so the message is set after the reload). Never silently
        // retry the user's stale edit.
        await get().loadConfig(selectedSiteId);
        set({ isConfigLoading: false, configError: 'Config changed on the server — reloaded the latest. Please re-apply your change.' });
        return false;
      }
      const message = err instanceof Error ? err.message : 'Failed to save config';
      set({ isConfigLoading: false, configError: message });
      console.error('saveConfig error:', err);
      return false;
    }
  },

  // B3a — shared plumbing for ONE canonical granular write. Requires configVersion (else
  // reload + bail), serializes overlapping writes, sends expectedVersion, applies the
  // returned config (config-column mutations — globals preserved) or globals, advances the
  // CAS token, and on 409 reloads D1 + surfaces a conflict (no silent retry). Returns true
  // only on a confirmed successful write.
  runCanonicalWrite: async (doWrite, opts): Promise<boolean> => {
    const { selectedSiteId, configVersion, configWriteInFlight } = get();
    if (!selectedSiteId) return false;
    // Serialize: one canonical write at a time (a second would race the version → 409).
    if (configWriteInFlight) return false;
    if (configVersion == null) {
      set({ configError: 'Config version not loaded — reloading from server. Please retry.' });
      await get().loadConfig(selectedSiteId);
      return false;
    }

    set({ configWriteInFlight: true, isConfigLoading: true, configError: null });
    try {
      const res = await doWrite(selectedSiteId, configVersion);
      const nextVersion = res.version ?? get().configVersion;
      if (opts?.kind === 'globals') {
        // Globals live in their own column; the response carries the merged globals.
        const prev = get().projectConfig;
        set({
          projectConfig: prev ? { ...prev, globals: (res as api.CanonicalGlobalsWriteResult).globals } : prev,
          configVersion: nextVersion,
          isConfigDirty: false,
          isConfigLoading: false,
          configWriteInFlight: false,
        });
      } else {
        // Config-column mutation: the returned `config` has NO globals (separate column) —
        // preserve the store's current globals so they aren't dropped on apply.
        const cfgRes = res as api.CanonicalConfigWriteResult;
        const prevGlobals = get().projectConfig?.globals;
        const nextConfig = {
          ...(cfgRes.config),
          globals: cfgRes.config.globals ?? prevGlobals,
        } as ProjectMonitoringConfig;
        set({
          projectConfig: nextConfig,
          configVersion: nextVersion,
          isConfigDirty: false,
          isConfigLoading: false,
          configWriteInFlight: false,
          ...(opts?.afterApply ? opts.afterApply(nextConfig) : {}),
        });
      }
      return true;
    } catch (err) {
      if (err instanceof api.ApiError && err.status === 409) {
        // Stale write — reload the latest D1 config, THEN surface the conflict (loadConfig
        // clears configError). Never silently retry the user's stale edit.
        await get().loadConfig(selectedSiteId);
        set({ isConfigLoading: false, configWriteInFlight: false, configError: 'Config changed on the server — reloaded the latest. Please re-apply your change.' });
        return false;
      }
      const message = err instanceof Error ? err.message : 'Failed to save config';
      set({ isConfigLoading: false, configWriteInFlight: false, configError: message });
      console.error('canonical write error:', err);
      return false;
    }
  },

  // Clear the live-mode offender overlay on mode switch; it is recomputed on the
  // next runtime-data update for the new mode.
  setDataMode: (mode: DataMode) => set({ dataMode: mode, advancedMatchedByRule: {} }),

  // B3a — canonical POST (a NEW datapoint may carry initial rules/rule_groups; allowed on
  // create). On success re-evaluate against sample data so the new point shows its value.
  addDataPointConfig: async (config: DataPointConfig): Promise<boolean> => {
    const { projectConfig, uploadedSampleData } = get();
    if (!projectConfig) return false;
    const ok = await get().runCanonicalWrite((siteId, ev) => api.addDatapointApi(siteId, config, ev));
    if (ok && uploadedSampleData && Object.keys(uploadedSampleData).length > 0) {
      get().updateRuntimeData(uploadedSampleData, new Date().toISOString());
    }
    return ok;
  },

  // B2 interim (B3b): existing-datapoint modal edit stays local-mutate + Publish. The bundled
  // metadata + nested rules/rule_groups save needs the B3b diff-sync decomposition, so this is
  // deliberately NOT a canonical write (map coordinate/metadata writes also defer to B3b).
  updateDataPointConfig: (id: string, updates: Partial<DataPointConfig>) => {
    const { projectConfig, uploadedSampleData } = get();
    if (!projectConfig) return;
    set({
      projectConfig: {
        ...projectConfig,
        datapoints: projectConfig.datapoints.map((dp) =>
          dp.id === id ? { ...dp, ...updates } : dp
        ),
      },
      isConfigDirty: true,
    });
    // Re-evaluate so updated rules/metrics take effect immediately
    if (uploadedSampleData && Object.keys(uploadedSampleData).length > 0) {
      get().updateRuntimeData(uploadedSampleData, new Date().toISOString());
    }
  },

  // B3a — canonical DELETE of one datapoint (removes its nested rules/groups with it).
  removeDataPointConfig: async (id: string): Promise<boolean> =>
    get().runCanonicalWrite((siteId, ev) => api.deleteDatapointApi(siteId, id, ev)),

  // B3a — standalone datapoint rule/rule-group actions map 1:1 to a canonical endpoint.
  // (No component calls these directly today — the datapoint modal bundles rules; these stay
  // correct for any future caller. They do NOT participate in the B3b modal diff-sync.)
  addAlarmRule: async (datapointId: string, rule: AlarmRule): Promise<boolean> =>
    get().runCanonicalWrite((siteId, ev) => api.addDatapointRuleApi(siteId, datapointId, rule, ev)),

  removeAlarmRule: async (datapointId: string, ruleId: string): Promise<boolean> =>
    get().runCanonicalWrite((siteId, ev) => api.deleteDatapointRuleApi(siteId, datapointId, ruleId, ev)),

  addAlarmRuleGroup: async (datapointId: string, group: AlarmRuleGroup): Promise<boolean> =>
    get().runCanonicalWrite((siteId, ev) => api.addRuleGroupApi(siteId, datapointId, group, ev)),

  removeAlarmRuleGroup: async (datapointId: string, groupId: string): Promise<boolean> =>
    get().runCanonicalWrite((siteId, ev) => api.deleteRuleGroupApi(siteId, datapointId, groupId, ev)),

  // B3a — optimistic LOCAL globals merge only (no write). Keeps controlled inputs responsive
  // while a value is mid-edit; persist with updateGlobals.
  setGlobalsLocal: (globalsPartial) => {
    const { projectConfig } = get();
    if (!projectConfig) return;
    set({
      projectConfig: {
        ...projectConfig,
        globals: { ...(projectConfig.globals ?? {}), ...globalsPartial } as NonNullable<ProjectMonitoringConfig['globals']>,
      },
    });
  },

  // B3a — canonical PATCH /globals with ONLY the changed keys (server shallow-merges). Applies
  // an optimistic local merge first (responsive inputs), then persists; 409/error reload reverts.
  updateGlobals: async (globalsPartial): Promise<boolean> => {
    const { projectConfig } = get();
    if (!projectConfig) return false;
    get().setGlobalsLocal(globalsPartial);
    return get().runCanonicalWrite(
      (siteId, ev) => api.patchGlobalsApi(siteId, globalsPartial, ev),
      { kind: 'globals' },
    );
  },

  // B3a — canonical POST advanced-rule. afterApply recomputes the display like the old
  // local mutation did (upload triggered-rules from the new config + sample data; clear the
  // live overlay). Sample data / schema captured here don't change during the write.
  addAdvancedRule: async (rule: AdvancedAlarmRule): Promise<boolean> => {
    const { projectConfig, uploadedSampleData, schema } = get();
    if (!projectConfig) return false;
    return get().runCanonicalWrite(
      (siteId, ev) => api.addAdvancedRuleApi(siteId, rule, ev),
      { afterApply: (cfg) => advancedDisplayAfterApply(cfg, uploadedSampleData, schema) },
    );
  },

  // B3a — canonical PATCH advanced-rule. A pure enable/disable toggle uses the narrow
  // `enabled` endpoint; any other change sends the FULL rule (merged onto the existing one,
  // since the endpoint replaces the whole rule — a partial would clobber other fields).
  updateAdvancedRule: async (id: string, updates: Partial<AdvancedAlarmRule>): Promise<boolean> => {
    const { projectConfig, uploadedSampleData, schema } = get();
    if (!projectConfig) return false;
    const existing = (projectConfig.advanced_rules ?? []).find((r) => r.id === id);
    if (!existing) return false;
    const onlyEnabled = Object.keys(updates).length === 1 && typeof updates.enabled === 'boolean';
    const merged = { ...existing, ...updates, id } as AdvancedAlarmRule;
    return get().runCanonicalWrite(
      (siteId, ev) => onlyEnabled
        ? api.setAdvancedRuleEnabledApi(siteId, id, updates.enabled as boolean, ev)
        : api.updateAdvancedRuleApi(siteId, id, merged, ev),
      { afterApply: (cfg) => advancedDisplayAfterApply(cfg, uploadedSampleData, schema) },
    );
  },

  // B3a — canonical DELETE advanced-rule.
  removeAdvancedRule: async (id: string): Promise<boolean> => {
    const { projectConfig, uploadedSampleData, schema } = get();
    if (!projectConfig) return false;
    return get().runCanonicalWrite(
      (siteId, ev) => api.deleteAdvancedRuleApi(siteId, id, ev),
      { afterApply: (cfg) => advancedDisplayAfterApply(cfg, uploadedSampleData, schema) },
    );
  },

  // Exclude-from-alarm: append a single {entity_id, metric} offender to an advanced rule's
  // exclusions, then persist via the canonical advanced-rule PATCH (B3a — full rule with the
  // new exclusion, NOT saveConfig). This modifies rule CONFIG only — it is NOT a
  // dismiss/resolve/acknowledge and never touches activeAlarms or alarm lifecycle state. The
  // evaluator skips excluded pairs, so the offender disappears from the overlay on next eval.
  addRuleExclusion: async (ruleId, exclusion) => {
    const { projectConfig, uploadedSampleData, schema, advancedMatchedByRule } = get();
    if (!projectConfig) return false;
    const rules = projectConfig.advanced_rules ?? [];
    const rule = rules.find((r) => r.id === ruleId);
    if (!rule) return false; // unknown rule → safe no-op

    const { entity_id, metric } = exclusion;
    const existing = rule.exclusions ?? [];
    // Dedupe by entity_id + metric — repeated Exclude clicks never add duplicates. Always
    // build a NEW exclusions array (fresh copy even on the dedupe path) so the updated rule
    // never shares the previous rule's exclusions reference — no in-place mutation.
    const already = existing.some((e) => e.entity_id === entity_id && e.metric === metric);
    const updatedExclusions = already ? [...existing] : [...existing, { entity_id, metric }];
    const updatedRule = { ...rule, exclusions: updatedExclusions } as AdvancedAlarmRule;

    // Immediate live-mode feedback: filter the excluded pair out of THIS rule's display-only
    // overlay (rather than clearing the whole overlay) so the row vanishes without waiting for
    // the server round-trip. On 409/error the D1 reload recomputes/reverts the overlay.
    const ruleMatches = advancedMatchedByRule[ruleId];
    if (ruleMatches) {
      set({
        advancedMatchedByRule: {
          ...advancedMatchedByRule,
          [ruleId]: ruleMatches.filter((m) => !(m.entity_id === entity_id && m.metric === metric)),
        },
      });
    }

    // Persist the full rule (with the new exclusion) via the canonical advanced-rule PATCH.
    // afterApply recomputes the upload-mode triggered-rules display but PRESERVES the optimistic
    // overlay above (it omits advancedMatchedByRule from the applied set). Returns true only on
    // a confirmed write; a 409/blocked/errored write returns false (and reloads D1).
    return get().runCanonicalWrite(
      (siteId, ev) => api.updateAdvancedRuleApi(siteId, ruleId, updatedRule, ev),
      {
        afterApply: (cfg) => {
          const data = uploadedSampleData && Object.keys(uploadedSampleData).length > 0 ? uploadedSampleData : null;
          return { triggeredAdvancedRules: data ? evaluateAdvancedRules(cfg.advanced_rules ?? [], data, schema) : get().triggeredAdvancedRules };
        },
      },
    );
  },

  // ============================================
  // Schema Actions
  // ============================================

  loadSchema: async (siteId: string) => {
    try {
      const schema = await api.getSchema(siteId);
      // Schema changed — drop the stale live offender overlay (evaluator input);
      // next updateRuntimeData recomputes against the new schema.
      set({ schema, isSchemaLoaded: !!schema, advancedMatchedByRule: {} });
    } catch (err) {
      console.error('loadSchema error:', err);
    }
  },

  setSchema: (schema: ParsedSchema) => {
    // Schema changed — clear the stale live offender overlay; recomputed on next
    // updateRuntimeData.
    set({ schema, isSchemaLoaded: true, advancedMatchedByRule: {} });
  },

  resetProjectState: () => {
    set({
      projectConfig: null,
      isConfigDirty: false,
      schema: null,
      isSchemaLoaded: false,
      runtimeStates: new Map(),
      lastRefreshAt: null,
      refreshError: null,
      uploadedSampleData: null,
      activeAlarms: [],
      triggeredAdvancedRules: [],
      advancedMatchedByRule: {},
      alarmHistory: [],
      invalidDatapointIds: [],
      schemaHash: '',
      sldDiagrams: [],
      activeSldId: null,
    });
  },

  setProjectConfig: (config: ProjectMonitoringConfig) => {
    // B2 — D1 is the source of truth for the ACTIVE site. Once its config has been
    // hydrated from D1, a (possibly stale) blob snapshot restored by a project load must
    // NOT overwrite it. Ignore the blob for the D1-authoritative active site; otherwise
    // (no D1 config yet, or a different/no active site) accept it as the initial value
    // (selectSite's loadConfig will then re-hydrate from D1 and take over).
    const { selectedSiteId, d1ConfigSiteId } = get();
    if (selectedSiteId && d1ConfigSiteId === selectedSiteId) {
      return; // keep the D1-hydrated config — no stale-blob clobber
    }
    const needsSync = (config.datapoints?.length ?? 0) > 0;
    // Whole config (incl. advanced_rules) replaced — drop the stale live offender
    // overlay; next updateRuntimeData recomputes from current data + new rules.
    set({ projectConfig: config, isConfigDirty: needsSync, advancedMatchedByRule: {} });
  },

  setUploadedSampleData: (data: Record<string, Record<string, number | string | null>>) => {
    set({ uploadedSampleData: data });
  },

  // ============================================
  // Data Actions
  // ============================================

  pushUploadData: async (jsonPayload: unknown) => {
    const { selectedSiteId, schema } = get();
    if (!selectedSiteId) return;

    set({ isRefreshing: true, refreshError: null });

    // 1. Parse sample data locally using schema — immediate evaluation
    const entityValues = extractEntityValues(jsonPayload, schema);
    const now = new Date().toISOString();

    if (Object.keys(entityValues).length > 0) {
      set({
        uploadedSampleData: entityValues,
        isRefreshing: false,
        providerStatus: 'connected',
        scadaConfig: {
          ...get().scadaConfig,
          isConnected: true,
          lastSync: now,
        },
      });
      // Evaluate all alarms (data point + advanced) immediately
      get().updateRuntimeData(entityValues, now);
    } else {
      set({ isRefreshing: false, refreshError: 'No entity data found in file' });
    }

    // 2. Also push to server for persistence (non-blocking)
    try {
      await api.pushData(selectedSiteId, jsonPayload);
    } catch (err) {
      console.warn('pushData to server failed (data evaluated locally):', err);
    }
  },

  refreshData: async () => {
    const { selectedSiteId, schema } = get();
    if (!selectedSiteId) return;

    set({ isRefreshing: true, refreshError: null });
    try {
      const latest = await api.getLatestData(selectedSiteId);

      // Extract entity values using schema (normalizes any JSON format —
      // both flat and timestamp-nested — into { entity_id → { metric → value } })
      const entityValues = extractEntityValues(latest.data, schema);
      const timestamp = latest.timestamp || new Date().toISOString();

      if (Object.keys(entityValues).length > 0) {
        get().updateRuntimeData(entityValues, timestamp);
      }

      // Merge server-side alarms if present
      if (latest.alarms?.length) {
        const existing = get().activeAlarms;
        const serverAlarmIds = new Set(latest.alarms.map((a: ActiveAlarmEvent) => a.id));
        const merged = [
          ...existing.filter((a) => !serverAlarmIds.has(a.id)),
          ...latest.alarms,
        ];
        set({ activeAlarms: merged });
      }

      set({
        isRefreshing: false,
        scadaConfig: {
          ...get().scadaConfig,
          lastSync: new Date().toISOString(),
          lastRefresh: new Date().toISOString(),
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Refresh failed';
      set({ isRefreshing: false, refreshError: message });
    }
  },

  updateRuntimeData: (entities, timestamp) => {
    const { projectConfig, selectedSiteId, schema, dataMode } = get();

    // In live mode, backend (DO) is the authoritative alarm evaluator.
    // Frontend only computes runtime display states (values + staleness), NOT alarms.
    if (dataMode === 'live') {
      const staleAfter = projectConfig?.globals?.stale_after_seconds ?? 300;
      const runtimeMap = evaluateAllDataPoints(
        projectConfig?.datapoints ?? [],
        entities,
        timestamp,
        staleAfter
      );
      // Do NOT compute alarms here — in live mode the DO is the authoritative
      // evaluator for BOTH data-point rules and advanced rules, delivering them
      // via activeAlarms (WS snapshot/alarm). Clear any triggeredAdvancedRules
      // left over from a previous upload-mode session so live advanced alarms
      // (which arrive through activeAlarms) aren't double-shown.
      //
      // We DO re-run the frontend advanced-rule evaluator over the same live data
      // the DO just evaluated, but ONLY to recover the structured offender list
      // (matched_metrics) for display. This overlay is display metadata keyed by
      // rule_id — it is NOT an alarm source and never creates/removes/counts an
      // alarm. The evaluator honors each rule's exclusions, so excluded entities
      // never appear in the offender list.
      const advancedMatchedByRule: Record<string, MatchedMetric[]> = {};
      for (const t of evaluateAdvancedRules(projectConfig?.advanced_rules ?? [], entities, schema)) {
        if (t.matched_metrics && t.matched_metrics.length > 0) {
          advancedMatchedByRule[t.rule_id] = t.matched_metrics;
        }
      }
      set({
        runtimeStates: runtimeMap,
        lastRefreshAt: timestamp,
        triggeredAdvancedRules: [],
        advancedMatchedByRule,
      });
      return;
    }

    // Upload/sample mode: full client-side evaluation (unchanged)
    const staleAfter = Infinity; // sample data never goes stale
    const runtimeMap = evaluateAllDataPoints(
      projectConfig?.datapoints ?? [],
      entities,
      timestamp,
      staleAfter
    );

    const advancedTriggered = evaluateAdvancedRules(
      projectConfig?.advanced_rules ?? [],
      entities,
      schema
    );

    const alarms: ActiveAlarmEvent[] = [];
    runtimeMap.forEach((runtime) => {
      const now = timestamp || new Date().toISOString();
      for (const rule of runtime.triggered_rules) {
        alarms.push({
          id: `${runtime.datapoint_id}_${rule.rule_id}`,
          site_id: selectedSiteId || '',
          datapoint_id: runtime.datapoint_id,
          rule_id: rule.rule_id,
          metric: rule.metric,
          operator: rule.op,
          threshold: rule.value,
          actual_value: typeof rule.actual === 'number' ? rule.actual : null,
          severity: rule.severity,
          state: 'active',
          message: rule.message,
          triggered_at: now,
        });
      }
      for (const group of runtime.triggered_groups) {
        alarms.push({
          id: `${runtime.datapoint_id}_${group.group_id}`,
          site_id: selectedSiteId || '',
          datapoint_id: runtime.datapoint_id,
          rule_group_id: group.group_id,
          metric: group.triggered_conditions[0]?.metric || '',
          operator: group.triggered_conditions[0]?.op || '',
          threshold: group.triggered_conditions[0]?.value || 0,
          actual_value: typeof group.triggered_conditions[0]?.actual === 'number' ? group.triggered_conditions[0].actual : null,
          severity: group.severity,
          state: 'active',
          message: group.message || group.group_name,
          triggered_at: now,
        });
      }
    });

    set({
      runtimeStates: runtimeMap,
      lastRefreshAt: timestamp,
      activeAlarms: alarms,
      triggeredAdvancedRules: advancedTriggered,
      // Upload/sample mode renders rich advanced rules from triggeredAdvancedRules
      // directly; the live-only overlay stays empty here.
      advancedMatchedByRule: {},
    });
  },

  // ============================================
  // Stream Actions
  // ============================================

  startStream: () => {
    const { selectedSiteId, alarmSoundEnabled, credentialStatus } = get();
    if (!selectedSiteId || streamManager) return;
    if (!credentialStatus?.hasCredentials) {
      console.warn('[Stream] Skipping WS — no credentials configured');
      return;
    }

    streamManager = createDataStream(
      { siteId: selectedSiteId },
      {
        onSnapshot: (snapshot) => {
          get().hydrateFromSnapshot(snapshot);
        },
        onData: (entities, ts) => {
          // Convert DO entity array to record format for updateRuntimeData
          const entityMap: Record<string, Record<string, number | string | null>> = {};
          for (const ent of entities) {
            entityMap[ent.entity_id] = ent.metrics;
          }
          get().updateRuntimeData(entityMap, new Date(ts).toISOString());
        },
        onAlarm: (alarm) => {
          set((state) => {
            const list = Array.isArray(state.activeAlarms) ? state.activeAlarms : [];
            // Drop any prior copy first so repeated events for the same alarm
            // never produce duplicates.
            const prior = list.find((a) => a.id === alarm.id);
            const withoutThis = list.filter((a) => a.id !== alarm.id);
            // Backward-compatible identity guard: an older deployed Worker (or a
            // transitional message) may omit rule_id/rule_group_id on a WS alarm
            // update. Preserve the prior entry's identity when the incoming update
            // lacks it so advanced-alarm display metadata stays mappable by rule_id.
            // Incoming values win when present.
            const merged = {
              ...alarm,
              rule_id: alarm.rule_id ?? prior?.rule_id,
              rule_group_id: alarm.rule_group_id ?? prior?.rule_group_id,
            };
            // Only OPEN alarms belong in the active list. A 'resolved' event (the
            // DO clearing a fault when data shows the condition no longer holds)
            // or 'dismissed' removes it from the active view immediately — no page
            // reload. The durable record still lives server-side (history endpoint);
            // this only touches the in-memory active list, not alarmHistory.
            const isOpen = merged.state === 'active' || merged.state === 'acknowledged';
            return { activeAlarms: isOpen ? [...withoutThis, merged] : withoutThis };
          });
          if (alarmSoundEnabled && alarm.state === 'active') {
            playAlarmSound(alarm.severity);
          }
        },
        onHealth: (health) => {
          set({
            providerHealth: health,
            monitoringUIState: get().deriveMonitoringUIState(),
          });
        },
        onSchemaUpdated: (schemaHash, invalidDatapointIds) => {
          set({ schemaHash, invalidDatapointIds });
        },
        onStatusChange: (connState, message) => {
          set({
            isStreaming: connState === 'connected',
            streamError: connState === 'error' ? (message || 'Stream error') : null,
            providerStatus: connState === 'connected' ? 'connected' : connState === 'error' ? 'error' : 'disconnected',
            monitoringUIState: get().deriveMonitoringUIState(),
          });
        },
        onError: (err) => {
          console.error('[Stream] Error:', err);
        },
      }
    );

    streamManager.start();
    set({ isStreaming: true, dataMode: 'live' });
  },

  stopStream: () => {
    if (streamManager) {
      streamManager.destroy();
      streamManager = null;
    }
    set({ isStreaming: false, streamError: null });
  },

  // ============================================
  // Live Mode Actions
  // ============================================

  hydrateFromSnapshot: (snapshot: WSSnapshotMessage) => {
    const entityMap: Record<string, Record<string, number | string | null>> = {};
    for (const ent of snapshot.entities) {
      entityMap[ent.entity_id] = ent.metrics;
    }
    const timestamp = new Date(snapshot.ts).toISOString();

    // Hydrate health
    const health = snapshot.health;
    const activeAlarms = snapshot.activeAlarms ?? [];

    // Update schema if present and hash differs
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updates: Record<string, any> = {
      providerHealth: health,
      activeAlarms,
      lastRefreshAt: timestamp,
      schemaHash: snapshot.schemaHash,
    };

    if (snapshot.schema && snapshot.schemaHash !== get().schemaHash) {
      // Convert DO schema format to ParsedSchema format if needed
      updates.schema = snapshot.schema;
      updates.isSchemaLoaded = true;
    }

    set(updates);

    // Run display-only evaluation (values + staleness) — no alarm eval in live mode
    get().updateRuntimeData(entityMap, timestamp);

    // Derive UI state after full hydration
    set({ monitoringUIState: get().deriveMonitoringUIState() });
  },

  startLive: async () => {
    const { selectedSiteId } = get();
    if (!selectedSiteId) return;
    try {
      await api.startLive(selectedSiteId);
      get().startStream();
    } catch (err) {
      console.error('[Live] Start failed:', err);
    }
  },

  stopLive: async () => {
    const { selectedSiteId } = get();
    if (!selectedSiteId) return;
    get().stopStream();
    try {
      await api.stopLive(selectedSiteId);
    } catch (err) {
      console.error('[Live] Stop failed:', err);
    }
    set({
      providerHealth: null,
      monitoringUIState: get().deriveMonitoringUIState(),
    });
  },

  refreshLive: async () => {
    const { selectedSiteId } = get();
    if (!selectedSiteId) return;
    try {
      await api.refreshLive(selectedSiteId);
    } catch (err) {
      console.error('[Live] Refresh failed:', err);
    }
  },

  discoverSchema: async () => {
    const { selectedSiteId } = get();
    if (!selectedSiteId) return;
    try {
      const schema = await api.discoverSchema(selectedSiteId);
      // Schema changed — clear the stale live offender overlay; recomputed on next
      // updateRuntimeData.
      set({ schema: schema as unknown as ParsedSchema, isSchemaLoaded: true, advancedMatchedByRule: {} });
    } catch (err) {
      console.error('[Discover] Schema discovery failed:', err);
      throw err;
    }
  },

  deriveMonitoringUIState: (): MonitoringUIState => {
    const { credentialStatus, providerHealth, isSchemaLoaded, projectConfig, isStreaming } = get();

    if (!credentialStatus?.hasCredentials) return 'NO_CREDENTIALS';

    if (providerHealth?.status === 'circuit_open') return 'CIRCUIT_OPEN';
    if (providerHealth?.status === 'error' || providerHealth?.status === 'disabled') return 'ERROR';

    if (isStreaming) return 'LIVE_ACTIVE';

    const hasDatapoints = (projectConfig?.datapoints?.length ?? 0) > 0;
    if (hasDatapoints) return 'DATAPOINTS_CONFIGURED';
    if (isSchemaLoaded) return 'SCHEMA_LOADED';

    // Credentials saved but nothing else done yet
    return 'CREDENTIALS_SAVED';
  },

  // ============================================
  // Credential Actions
  // ============================================

  loadProviders: async () => {
    if (get().providers.length > 0) return; // already loaded
    try {
      const result = await api.listProviders();
      set({ providers: result.providers });
    } catch (err) {
      console.error('loadProviders error:', err);
    }
  },

  loadCredentialStatus: async (siteId: string) => {
    try {
      const status = await api.getCredentialStatus(siteId);
      set({
        credentialStatus: status,
        dataMode: status.hasCredentials ? 'live' : 'upload',
      });
      set({ monitoringUIState: get().deriveMonitoringUIState() });
    } catch (err) {
      console.error('loadCredentialStatus error:', err);
    }
  },

  testConnection: async () => {
    const { selectedSiteId, credentialStatus } = get();
    if (!selectedSiteId) return false;

    set({ providerStatus: 'testing' });
    try {
      const provider = credentialStatus?.provider
        ?? credentialStatus?.credentials?.[0]?.provider_name;
      const result = await api.testConnection(selectedSiteId, provider);
      set({
        providerStatus: result.ok ? 'connected' : 'error',
        scadaConfig: {
          ...get().scadaConfig,
          isConnected: result.ok,
          error: result.ok ? undefined : result.message,
        },
      });
      return result.ok;
    } catch (err) {
      set({
        providerStatus: 'error',
        scadaConfig: {
          ...get().scadaConfig,
          isConnected: false,
          error: err instanceof Error ? err.message : 'Connection test failed',
        },
      });
      return false;
    }
  },

  // ============================================
  // Alarm Actions
  // ============================================

  loadAlarms: async (siteId: string) => {
    try {
      // The list endpoint filters by a single state (defaults to 'active'), so
      // fetch BOTH open states — active + acknowledged — and dedupe by id. This
      // ensures the rail/active surfaces show all open alarms (acknowledged
      // included) on load. Resolved/dismissed are never requested.
      const [activeAlarms, acknowledgedAlarms] = await Promise.all([
        api.listAlarms(siteId, 'active'),
        api.listAlarms(siteId, 'acknowledged'),
      ]);
      const byId = new Map<string, ActiveAlarmEvent>();
      for (const a of [
        ...(Array.isArray(activeAlarms) ? activeAlarms : []),
        ...(Array.isArray(acknowledgedAlarms) ? acknowledgedAlarms : []),
      ]) {
        byId.set(a.id, a);
      }
      const serverList = Array.from(byId.values());

      // Merge semantics. The server (active + acknowledged) is authoritative for
      // server-backed alarms, so an alarm absent from BOTH responses has been
      // resolved/dismissed and must NOT survive.
      //   - LIVE mode: every activeAlarms entry is server-backed (the live-mode
      //     updateRuntimeData branch never adds client-evaluated alarms), so we
      //     keep zero carry-over — the server list fully replaces the open set.
      //   - UPLOAD/sample mode: alarms are computed client-side (updateRuntimeData)
      //     and the server doesn't track them, so preserve those not on the server.
      // Dedupe stays by id.
      const existing = get().activeAlarms;
      const serverIds = new Set(serverList.map((a) => a.id));
      const clientOnly = get().dataMode === 'live'
        ? []
        : existing.filter((a) => !serverIds.has(a.id));
      set({ activeAlarms: [...serverList, ...clientOnly] });
    } catch (err) {
      console.error('loadAlarms error:', err);
      // On error, keep existing alarms instead of wiping them
    }
  },

  acknowledgeAlarm: async (alarmId: string, notes?: string) => {
    const { selectedSiteId } = get();
    if (!selectedSiteId) return;

    try {
      await api.acknowledgeAlarm(selectedSiteId, alarmId, notes);
      set((state) => {
        const alarms = Array.isArray(state.activeAlarms) ? state.activeAlarms : [];
        return {
          activeAlarms: alarms.map((a) =>
            a.id === alarmId
              ? { ...a, state: 'acknowledged' as const, acknowledged_at: new Date().toISOString() }
              : a
          ),
        };
      });
    } catch (err) {
      console.error('acknowledgeAlarm error:', err);
    }
  },

  dismissAlarm: (id: string) => {
    set((state) => {
      const alarms = Array.isArray(state.activeAlarms) ? state.activeAlarms : [];
      const alarm = alarms.find((a) => a.id === id);
      return {
        activeAlarms: alarms.filter((a) => a.id !== id),
        alarmHistory: alarm ? [...state.alarmHistory, alarm] : state.alarmHistory,
      };
    });
  },

  clearAlarmHistory: () => set({ alarmHistory: [] }),

  setAlarmSoundEnabled: (enabled: boolean) => {
    setSoundEnabled(enabled);
    set({ alarmSoundEnabled: enabled });
  },

  // ============================================
  // Site Actions
  // ============================================

  fetchScadaSites: async () => {
    const token = useAuthStore.getState().token;
    if (!token) return; // Not logged in — skip silently
    set({ isFetchingSites: true });
    try {
      const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://api.soleadlab.com';
      const res = await fetch(`${API_BASE}/api/v2/sites?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json() as { data?: { items?: Array<{ id: string; name: string; location?: string; client_id?: string }> } };
      const items = body?.data?.items || [];
      set({
        scadaSites: items.map((s) => ({ id: s.id, name: s.name, description: s.location, client_id: s.client_id })),
        isFetchingSites: false,
        scadaConfig: { ...get().scadaConfig, isConnected: true, error: undefined },
      });
    } catch (err) {
      console.error('Failed to fetch sites:', err);
      set({
        isFetchingSites: false,
        scadaConfig: {
          ...get().scadaConfig,
          isConnected: false,
          error: err instanceof Error ? err.message : 'Connection failed',
        },
      });
    }
  },

  selectSite: async (siteId) => {
    // Stop any existing stream — destroy directly to avoid a separate set() call
    if (streamManager) {
      streamManager.destroy();
      streamManager = null;
    }

    // Preserve monitoring data that may have been restored by loadProject()
    const { schema: existingSchema, isSchemaLoaded: existingSchemaLoaded,
            uploadedSampleData: existingSampleData, runtimeStates: existingRuntime,
            projectConfig: existingConfig } = get();

    // Single merged set() call — reduces synchronous re-render cascade
    set({
      selectedSiteId: siteId,
      // Keep config/schema/runtime if already restored from project load
      projectConfig: existingConfig,
      // B2 — reset the CAS token + D1-authority marker for the new site; loadConfig() below
      // re-hydrates them from D1 (a stale prior-site version must never be reused).
      configVersion: null,
      d1ConfigSiteId: null,
      schema: existingSchema,
      isSchemaLoaded: existingSchemaLoaded,
      runtimeStates: existingRuntime,
      // Always clear alarms — loadAlarms() will fetch fresh ones for the new site
      activeAlarms: [],
      triggeredAdvancedRules: [],
      advancedMatchedByRule: {},
      credentialStatus: null,
      uploadedSampleData: existingSampleData,
      // Reset auto-refresh + stream state (merged from stopStream)
      isAutoRefreshEnabled: false,
      isStreaming: false,
      streamError: null,
      scadaConfig: { ...get().scadaConfig, siteId: siteId ?? undefined },
    });

    // Load config and schema for the new site, properly sequenced
    if (siteId) {
      // B2 — D1 monitoring_configs is the source of truth. ALWAYS hydrate from D1 (even if
      // a project/blob config was restored) so panels/map render the canonical config and
      // its version. The legacy blob→D1 auto-sync (which could wholesale-clobber D1-only
      // rules — e.g. wipe Mahanaim/Shoval 2, whose blob is empty) is REMOVED: no automatic
      // write happens on site selection.
      await get().loadConfig(siteId);

      // Load schema, credentials, alarms in parallel (independent)
      await Promise.all([
        // Only fetch schema from server if not already loaded (e.g. from project)
        !existingSchemaLoaded ? get().loadSchema(siteId) : Promise.resolve(),
        get().loadCredentialStatus(siteId),
        get().loadAlarms(siteId),
      ]);

      // Now refresh data — config is loaded so datapoints are available
      // If we already have sample data from project load, re-evaluate with loaded config
      const { uploadedSampleData, credentialStatus } = get();
      if (uploadedSampleData && Object.keys(uploadedSampleData).length > 0) {
        get().updateRuntimeData(uploadedSampleData, new Date().toISOString());
      } else {
        await get().refreshData();
      }

      // Auto-connect to live stream if credentials are configured
      if (credentialStatus?.hasCredentials) {
        try {
          await get().startLive();
        } catch {
          // Non-fatal — user can manually retry
        }
      }
    }
  },

  addScadaSite: async (name, description) => {
    const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://api.soleadlab.com';
    const token = useAuthStore.getState().token;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    // Find a client_id from existing sites
    let clientId = get().scadaSites.find((s) => s.client_id)?.client_id;

    // If no sites exist, fetch clients list from API
    if (!clientId) {
      try {
        const clientsRes = await fetch(`${API_BASE}/api/v2/clients`, { headers });
        if (clientsRes.ok) {
          const clientsBody = await clientsRes.json() as { data?: Array<{ id: string; name: string }> };
          const clients = clientsBody?.data || [];
          clientId = clients[0]?.id;
        }
      } catch {
        // ignore, will fail below
      }
    }

    if (!clientId) {
      throw new Error('No client available. Please contact an administrator.');
    }

    try {
      const res = await fetch(`${API_BASE}/api/v2/sites`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name, clientId, location: description }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error?.message || `HTTP ${res.status}`);
      }
      const body = await res.json() as { data?: { id: string; name: string; client_id: string; location?: string } };
      const site = body?.data;
      if (site) {
        set((state) => ({
          scadaSites: [...state.scadaSites, { id: site.id, name: site.name, description: site.location, client_id: site.client_id }],
          selectedSiteId: site.id,
        }));
        // Load config/schema for the new site
        get().selectSite(site.id);
      }
    } catch (err) {
      console.error('Failed to create site:', err);
      throw err;
    }
  },

  deleteScadaSite: async (siteId) => {
    const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://api.soleadlab.com';
    const token = useAuthStore.getState().token;

    try {
      const res = await fetch(`${API_BASE}/api/v2/sites/${siteId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error?.message || `HTTP ${res.status}`);
      }
    } catch (err) {
      console.error('Failed to delete site:', err);
    }

    set((state) => ({
      scadaSites: state.scadaSites.filter((s) => s.id !== siteId),
      selectedSiteId: state.selectedSiteId === siteId ? null : state.selectedSiteId,
    }));
  },

  // ============================================
  // SLD Actions
  // ============================================

  addSLD: (sld) =>
    set((state) => ({
      sldDiagrams: [...state.sldDiagrams, sld],
    })),

  updateSLD: (id, updates) =>
    set((state) => ({
      sldDiagrams: state.sldDiagrams.map((s) =>
        s.id === id ? { ...s, ...updates } : s
      ),
    })),

  removeSLD: (id) =>
    set((state) => ({
      sldDiagrams: state.sldDiagrams.filter((s) => s.id !== id),
      activeSldId: state.activeSldId === id ? null : state.activeSldId,
    })),

  setActiveSLD: (id) => set({ activeSldId: id }),

  // ============================================
  // Legacy Compat Actions
  // ============================================

  setScadaConfig: (config) =>
    set((state) => ({
      scadaConfig: { ...state.scadaConfig, ...config },
    })),

  setAutoRefresh: (enabled) => {
    if (enabled) {
      get().startStream();
    } else {
      get().stopStream();
    }
    set({ isAutoRefreshEnabled: enabled });
  },

  addDataPoint: (dataPoint) =>
    set((state) => ({
      dataPoints: [...state.dataPoints, dataPoint],
    })),

  updateDataPoint: (id, updates) =>
    set((state) => ({
      dataPoints: state.dataPoints.map((dp) =>
        dp.id === id ? { ...dp, ...updates } : dp
      ),
    })),

  removeDataPoint: (id) =>
    set((state) => ({
      dataPoints: state.dataPoints.filter((dp) => dp.id !== id),
    })),

  setDataPointValue: (id, value) => {
    set((state) => ({
      dataPoints: state.dataPoints.map((dp) =>
        dp.id === id
          ? { ...dp, currentValue: value, lastUpdated: new Date().toISOString() }
          : dp
      ),
    }));
  },

  addAlarmToDataPoint: (dataPointId, alarm) =>
    set((state) => ({
      dataPoints: state.dataPoints.map((dp) =>
        dp.id === dataPointId
          ? { ...dp, alarms: [...dp.alarms, alarm] }
          : dp
      ),
    })),

  updateAlarm: (dataPointId, alarmId, updates) =>
    set((state) => ({
      dataPoints: state.dataPoints.map((dp) =>
        dp.id === dataPointId
          ? {
              ...dp,
              alarms: dp.alarms.map((a) =>
                a.id === alarmId ? { ...a, ...updates } : a
              ),
            }
          : dp
      ),
    })),

  removeAlarm: (dataPointId, alarmId) =>
    set((state) => ({
      dataPoints: state.dataPoints.map((dp) =>
        dp.id === dataPointId
          ? { ...dp, alarms: dp.alarms.filter((a) => a.id !== alarmId) }
          : dp
      ),
    })),

  triggerAlarm: (_alarm) => {
    // Legacy: no-op — alarms now managed via activeAlarms from backend
  },

  setRefreshing: (refreshing) => set({ isRefreshing: refreshing }),

  loadMonitoringConfig: (config) => {
    set({
      dataPoints: config.dataPoints ?? [],
      sldDiagrams: config.sldDiagrams ?? [],
      scadaConfig: { ...defaultScadaConfig, ...config.scadaConfig },
    });
  },

  exportMonitoringConfig: () => {
    const { dataPoints, sldDiagrams, scadaConfig } = get();
    return {
      dataPoints: dataPoints.map((dp) => ({
        ...dp,
        currentValue: undefined,
        lastUpdated: undefined,
        status: undefined,
      })),
      sldDiagrams,
      scadaConfig: {
        ...scadaConfig,
        isConnected: false,
        lastSync: undefined,
        error: undefined,
      },
    };
  },
}));
